import { ReactNode, createContext, useEffect, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import {
  TickmillCompaniesEnum,
  getTickmillCompanyByHostname,
  getTickmillSupportEmail,
  getTickmillSupportPhone,
} from '@utils/companyName.utils'

import { SupportContactsDto } from '@model/SupportContactsDto'

import { useGuardedContext } from '@global/useGuardedContext'

import { Loading } from '@shared/components/Loading/Loading'

import { useSessionEntity } from './EntityContext'

type SupportContactsContextType = SupportContactsDto | undefined

const SupportContactsContext = createContext<SupportContactsContextType>(undefined)
SupportContactsContext.displayName = 'SupportContactsContext'

interface Props {
  children: ReactNode
}

export function SupportContactsProvider({ children }: Props): React.ReactElement<any> {
  const [supportContacts, setSupportContacts] = useState<SupportContactsDto>({
    supportPhoneNumber: '+357 25041710',
    supportEmail: 'support@tickmill.com',
  })
  const [loading, setLoading] = useState(true)
  const apiClient = useApiClient(ClientApiClient)
  const entity = useSessionEntity()

  useEffect(() => {
    const fetchSupportContacts = async () => {
      try {
        if (entity !== TickmillCompaniesEnum.GENERIC) {
          const contacts = await apiClient.getSupportContacts(entity)
          setSupportContacts(contacts)
        }
      } catch (error: unknown) {
        console.error('Failed to fetch support contacts:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchSupportContacts()
  }, [apiClient, entity])

  return (
    <SupportContactsContext.Provider value={supportContacts}>
      {loading ? <Loading isLoading={loading} showLoadingIcon /> : children}
    </SupportContactsContext.Provider>
  )
}

export const useSupportContactsContext = (): SupportContactsContextType => {
  return useGuardedContext(SupportContactsContext)
}

export const useSupportContacts = (): SupportContactsDto => {
  const supportContacts = useSupportContactsContext()
  if (!supportContacts || !supportContacts.supportEmail) {
    const entity = getTickmillCompanyByHostname(true)
    return {
      supportPhoneNumber: getTickmillSupportPhone(entity),
      supportEmail: getTickmillSupportEmail(entity),
    }
  }

  return supportContacts
}
